import React, { useEffect, useState } from 'react';
import { Button, Dropdown, DropdownProps, Switch } from 'antd';
import { MenuProps } from 'antd/lib';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { SettingService } from 'features/userSettings';
import { ReactComponent as PinnedIcon } from 'shared/assets/icons/dynamicColor/pin-button.svg';
import { ReactComponent as TimesheetsSettingIcon } from 'shared/assets/icons/dynamicColor/settingsIcon.svg';
import { UNHIDDABLE_COLUMNS, UNPINNABLE_COLUMNS } from '../../TimesheetsTable.const';

import styles from './TimesheetColumnsDropdown.module.scss';

interface TimesheetColumnsDropdownProps {
  columns: { key: string; label: string }[];
  columnsVisibilityMap: Record<string, boolean>;
  setColumnsVisibilityMap: (keys: Record<string, boolean>) => void;
  pinnedColumns: Record<string, boolean>;
  setPinnedColumns: (keys: Record<string, boolean>) => void;
}

export const TimesheetColumnsDropdown = ({
  columns,
  columnsVisibilityMap,
  setColumnsVisibilityMap,
  pinnedColumns,
  setPinnedColumns,
}: TimesheetColumnsDropdownProps): React.ReactElement => {
  const { t } = useTranslation('Timesheet');

  const [open, setOpen] = useState(false);

  const handleOpenChange: DropdownProps['onOpenChange'] = (nextOpen, info) => {
    if (info.source === 'trigger' || nextOpen) {
      setOpen(nextOpen);
    }
  };

  const onChangePinnedColumns = (checked: boolean, key: string): void => {
    const newPinned = { ...pinnedColumns, [key]: checked };
    setPinnedColumns(newPinned);
    SettingService.saveLocalSetting('timesheetPinnedColumns', newPinned);
  };

  useEffect(() => {
    const stored: Record<string, boolean> | null = SettingService.getLocalSetting('timesheetColumnsVisibilityMap');
    if (!stored) {
      SettingService.saveLocalSetting('timesheetColumnsVisibilityMap', columnsVisibilityMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeSelectedColumns = (checked: boolean, key: string): void => {
    const newSelected = { ...columnsVisibilityMap, [key]: checked };
    setColumnsVisibilityMap(newSelected);
    SettingService.saveLocalSetting('timesheetColumnsVisibilityMap', newSelected);
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'columnsTitle',
      label: <div className={styles.dropdownTitleLabel}>{t('header.columnsDropdown.label')}</div>,
    },
    ...columns.map((col) => ({
      key: col.key,
      label: (
        <div className={styles.columnBlock}>
          <span>{col.label}</span>
          <div className={styles.actions}>
            <Switch
              checked={!!columnsVisibilityMap[col.key]}
              disabled={UNHIDDABLE_COLUMNS.includes(col.key)}
              onChange={(checked) => onChangeSelectedColumns(checked, col.key)}
              size="small"
            />
            <Button
              disabled={UNPINNABLE_COLUMNS.includes(col.key)}
              className={styles.pinButton}
              type="text"
              variant="outlined"
              icon={
                <PinnedIcon
                  className={pinnedColumns[col.key] ? styles.pinnedIcon : styles.unpinnedIcon}
                  onClick={() => onChangePinnedColumns(!pinnedColumns[col.key], col.key)}
                />
              }
            />
          </div>
        </div>
      ),
    })),
  ];

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomLeft"
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName={styles.dropdownContainer}
    >
      <Button
        icon={<TimesheetsSettingIcon className={styles.icon} />}
        className={classNames(styles.openDropdownBtn, { [styles.activeDropdownBtn]: open })}
      >
        {t('header.columnsDropdown.label')}
      </Button>
    </Dropdown>
  );
};
